import { useState, useRef, useEffect } from "react";
import { useContent } from "@/lib/content-context";
import { fileUrl } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import {
    ArrowRight, ChevronDown, Mail, Phone, MapPin, Clock,
    MessageCircle, Briefcase, Truck, Building2, Store, Camera,
    Users, HelpCircle, Check, CheckCircle2, ShieldCheck,
    Instagram, Linkedin, Youtube,
} from "lucide-react";

const REASONS = [
    { id: "feedback", label: "Feedback", desc: "Compliments or concerns", icon: MessageCircle, subject: "Customer Feedback", placeholder: "Share your experience — what you loved, what we can do better…" },
    { id: "official", label: "Official", desc: "Formal correspondence", icon: Briefcase, subject: "Official Inquiry", placeholder: "Describe your inquiry and include any reference details…" },
    { id: "supply", label: "Supply Vendor", desc: "Products or raw materials", icon: Truck, subject: "Product Pitch", placeholder: "Tell us about your product, MOQ, pricing, and how it fits our menu…" },
    { id: "realestate", label: "Real Estate", desc: "Locations & properties", icon: Building2, subject: "Property Inquiry", placeholder: "Describe the property — location, area (sq ft), floor, ownership status…" },
    { id: "franchise", label: "Franchise", desc: "Own a Cafe Hideout", icon: Store, subject: "Franchise Inquiry", placeholder: "Tell us the city or area you have in mind and a bit about yourself…" },
    { id: "media", label: "Media & Collab", desc: "Press, content & partnerships", icon: Camera, subject: "Press / Collab Inquiry", placeholder: "Share your publication, platform, or collab idea and what you have in mind…" },
    { id: "events", label: "Events & Catering", desc: "Bulk orders or events", icon: Users, subject: "Events & Catering Request", placeholder: "Tell us about the event — date, location, expected headcount, and requirements…" },
    { id: "other", label: "Other", desc: "Something else entirely", icon: HelpCircle, subject: "General Inquiry", placeholder: "Tell us whatever's on your mind. We'll figure it out together…" },
];

const SOURCES = ["Instagram", "Word of Mouth", "Google", "Visited a Cafe", "Other"];

const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function Contact() {
    const { content } = useContent();
    const [reasonId, setReasonId] = useState("");
    const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", subject: "", message: "", source: "Instagram", consent: false });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const subjectTouched = useRef(false);

    useEffect(() => {
        if (!reasonId) return;
        const r = REASONS.find((x) => x.id === reasonId);
        if (r && !subjectTouched.current) {
            setForm((f) => ({ ...f, subject: r.subject }));
        }
    }, [reasonId]);

    if (!content) return null;
    const c = content.contact || {};
    const reason = REASONS.find((r) => r.id === reasonId);
    const waNumber = (c.whatsapp || "917292959595").replace(/\D/g, "");
    const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent("Hi Cafe Hideout!")}`;
    const formActive = !!reasonId;
    const social = content.footer?.social || {};

    const submit = async (e) => {
        e.preventDefault();
        if (!reasonId) { toast.error("Pick a reason first."); return; }
        if (!form.name || !form.email || !form.phone || !form.city || !form.message) { toast.error("Please fill all required fields."); return; }
        if (!form.consent) { toast.error("Please accept the consent checkbox."); return; }
        setSubmitting(true);
        try {
            await api.post("/contact", { ...form, reason: reason.label });
            setDone(true);
            setForm({ name: "", phone: "", email: "", city: "", subject: "", message: "", source: "Instagram", consent: false });
        } catch {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <SEO title="Contact" description={c.intro} image={fileUrl(c.hero_image)} path="/contact" />

            {/* HERO */}
            <section className="relative w-full flex items-center px-6 md:px-12 pt-28 md:pt-32" style={{ minHeight: "55vh" }} data-testid="contact-hero">
                <div className="absolute inset-0 overflow-hidden" style={{ minHeight: "55vh" }}>
                    <img src={fileUrl(c.hero_image)} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "rgba(26,15,0,0.4)" }} />
                </div>
                <div className="relative z-10 max-w-3xl py-16 md:py-24">
                    <Reveal>
                        <div className="tracked text-gold text-[10px] mb-6">{c.label}</div>
                        <h1 className="font-editorial font-bold text-cream leading-[0.98] mb-6" style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)" }} data-testid="contact-title">
                            {c.title}
                        </h1>
                        <p className="font-body text-cream/85 max-w-xl text-base md:text-lg leading-[1.7] mb-10">
                            {c.intro}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:flex-wrap">
                            <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-whatsapp"
                                className="inline-flex items-center justify-center gap-2.5 px-7 tracked text-[11px] font-semibold transition-all duration-300 hover:gap-3.5"
                                style={{ backgroundColor: "#c8a96e", color: "#1a0f00", minHeight: 52, fontFamily: "'Poppins', sans-serif" }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#b8994a")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#c8a96e")}
                            >
                                <WhatsAppIcon size={18} /> Chat on WhatsApp
                            </a>
                            <button
                                onClick={() => scrollToId("contact-form")}
                                data-testid="hero-scroll-form"
                                className="inline-flex items-center justify-center gap-2 px-7 tracked text-[11px] transition-all duration-300 hover:bg-cream/15"
                                style={{ minHeight: 52, color: "#f5e6c8", border: "1px solid rgba(245,230,200,0.7)" }}
                            >
                                Send a Message <ChevronDown size={14} />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 1 — QUICK CONNECT BAR */}
            <section className="px-6 md:px-12 py-16 md:py-20" style={{ backgroundColor: "#1a0f00" }} data-testid="quick-connect">
                <div className="max-w-[1320px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    <QuickCard icon={<WhatsAppIcon size={26} color="#c8a96e" />} title="WhatsApp Us" body="Fastest way to reach us. We typically respond within a few hours." cta={<a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 tracked text-[10px] px-5 py-3 transition-colors hover:bg-gold hover:text-coffee" style={{ minHeight: 44, color: "#c8a96e", border: "1px solid rgba(200,169,110,0.5)" }} data-testid="card-whatsapp">Open WhatsApp <ArrowRight size={12} /></a>} />
                    <QuickCard icon={<Mail size={26} strokeWidth={1.3} className="text-gold" />} title="Email Us" body="For detailed inquiries, proposals, or official correspondence." cta={<a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 tracked text-[10px] px-5 py-3 transition-colors hover:bg-gold hover:text-coffee" style={{ minHeight: 44, color: "#c8a96e", border: "1px solid rgba(200,169,110,0.5)" }} data-testid="card-email">{c.email} <ArrowRight size={12} /></a>} />
                    <QuickCard icon={<Instagram size={26} strokeWidth={1.3} className="text-gold" />} title="DM on Instagram" body="Slide into our DMs. We love hearing from our community." cta={<a href={c.instagram_url || "https://instagram.com/hideout.india"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 tracked text-[10px] px-5 py-3 transition-colors hover:bg-gold hover:text-coffee" style={{ minHeight: 44, color: "#c8a96e", border: "1px solid rgba(200,169,110,0.5)" }} data-testid="card-instagram">{c.instagram_handle || "@hideout.india"} <ArrowRight size={12} /></a>} />
                </div>
            </section>

            {/* SECTION 2 — REASON SELECTOR */}
            <section id="reason-selector" className="px-6 md:px-12 pt-20 md:pt-28 pb-0" style={{ backgroundColor: "#faf6ef" }} data-testid="reason-selector">
                <div className="max-w-[1280px] mx-auto text-center">
                    <Reveal>
                        <div className="tracked text-gold text-[10px] mb-5">Tell Us Why</div>
                        <h2 className="font-editorial font-bold text-coffee leading-[1.05]" style={{ fontSize: "clamp(2rem, 4vw, 2.6rem)" }}>What brings you here today?</h2>
                        <p className="font-body text-coffee/65 text-[15px] mt-4 max-w-xl mx-auto">Pick the reason that fits — we'll make sure the right team sees your message.</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-12">
                            {REASONS.map((r) => {
                                const Icon = r.icon;
                                const selected = r.id === reasonId;
                                return (
                                    <button
                                        key={r.id}
                                        onClick={() => { setReasonId(r.id); subjectTouched.current = false; }}
                                        data-testid={`reason-${r.id}`}
                                        className={`relative text-left transition-all duration-300 px-4 py-5 ${selected ? "" : "hover:bg-[#fdf8f0]"}`}
                                        style={{
                                            background: selected ? "#c8a96e" : "#fff",
                                            border: selected ? "1px solid #1a0f00" : "1px solid rgba(26,15,0,0.15)",
                                            borderRadius: 12,
                                            color: "#1a0f00",
                                        }}
                                    >
                                        {selected && (
                                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-coffee flex items-center justify-center">
                                                <Check size={11} className="text-gold" strokeWidth={2.5} />
                                            </div>
                                        )}
                                        <Icon size={26} strokeWidth={1.3} className={selected ? "text-coffee" : "text-gold"} />
                                        <div className="font-semibold text-[14px] mt-3" style={{ fontFamily: "'Poppins',sans-serif" }}>{r.label}</div>
                                        <div className="text-[12px] opacity-70 mt-1 leading-snug">{r.desc}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 3 — FORM */}
            <section id="contact-form" className="px-6 md:px-12 pt-12 pb-24 md:pb-28" style={{ backgroundColor: "#faf6ef" }}>
                <div className="max-w-[720px] mx-auto">
                    <Reveal>
                        <div
                            className="bg-white p-7 md:p-10 transition-all duration-300"
                            style={{
                                border: "1px solid rgba(200,169,110,0.6)",
                                borderRadius: 16,
                                opacity: formActive ? 1 : 0.5,
                                pointerEvents: formActive ? "auto" : "none",
                                boxShadow: "0 30px 80px -45px rgba(184,141,82,0.3)",
                            }}
                            data-testid="contact-form-card"
                        >
                            {done ? (
                                <div className="text-center py-8" data-testid="contact-success">
                                    <CheckCircle2 size={42} strokeWidth={1.2} className="text-gold mx-auto mb-6" />
                                    <p className="italic text-2xl md:text-[28px] leading-[1.4]" style={{ color: "#8a6b3d", fontFamily: "'Open Sans', Georgia, serif" }}>
                                        Message received. We'll get back to you within 48 hours. Thank you for reaching out.
                                    </p>
                                    <button onClick={() => setDone(false)} className="btn-pill btn-pill-coffee mt-8" data-testid="contact-submit-another">Send Another <ArrowRight size={14} /></button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-7" data-testid="contact-form-element">
                                    {!formActive && (
                                        <div className="text-center tracked text-coffee/55 text-[10px] py-2">
                                            ↑ Pick a reason above to continue
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                                        <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" required />
                                        <Field label="Phone Number *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" required />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                                        <Field label="Email Address *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" required />
                                        <Field label="City *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="contact-city" required />
                                    </div>

                                    {reason && (
                                        <div>
                                            <label className="tracked text-coffee/60 text-[10px] block mb-3">Reason For Contact *</label>
                                            <div className="flex items-center justify-between flex-wrap gap-3" data-testid="contact-reason-pill">
                                                <span className="inline-flex items-center gap-2 px-4 py-2.5 tracked text-[11px] font-semibold" style={{ background: "#c8a96e", color: "#1a0f00", fontFamily: "'Poppins',sans-serif" }}>
                                                    <Check size={12} strokeWidth={2.5} /> {reason.label}
                                                </span>
                                                <button type="button" onClick={() => scrollToId("reason-selector")} className="tracked text-[10px] text-coffee/55 hover:text-gold transition-colors gold-underline" data-testid="contact-change-reason">
                                                    Change
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <Field label="Subject" value={form.subject} onChange={(v) => { subjectTouched.current = true; setForm({ ...form, subject: v }); }} testid="contact-subject" />

                                    <Field label="Your Message *" textarea rows={5} value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="contact-message" required placeholder={reason ? reason.placeholder : "Tell us what's on your mind…"} />

                                    <div>
                                        <label className="tracked text-coffee/60 text-[10px] block mb-3">How did you hear about us?</label>
                                        <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="contact-source"
                                            className="w-full bg-transparent border-b border-coffee/30 focus:border-gold py-3 text-coffee font-body outline-none">
                                            {SOURCES.map((s) => <option key={s}>{s}</option>)}
                                        </select>
                                    </div>

                                    <label className="flex items-start gap-3 cursor-pointer pt-2">
                                        <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="contact-consent" className="mt-1 accent-coffee" required />
                                        <span className="font-body text-coffee/70 text-sm leading-relaxed">
                                            I agree to be contacted by the Cafe Hideout team regarding this message.
                                        </span>
                                    </label>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full tracked text-[11px] font-bold transition-colors disabled:opacity-50"
                                        data-testid="contact-submit"
                                        style={{ background: "#c8a96e", color: "#1a0f00", minHeight: 52, fontFamily: "'Poppins',sans-serif" }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = "#b8994a")}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = "#c8a96e")}
                                    >
                                        {submitting ? "SENDING…" : "SEND MESSAGE"}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>

                    {/* Reassurance strip */}
                    <Reveal delay={0.15}>
                        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-3 text-center" data-testid="contact-reassurance">
                            <div className="flex items-center justify-center gap-2.5">
                                <Clock size={16} strokeWidth={1.4} className="text-gold" />
                                <span className="tracked text-coffee/60 text-[10px]">Reply within 48 hrs</span>
                            </div>
                            <div className="flex items-center justify-center gap-2.5">
                                <ShieldCheck size={16} strokeWidth={1.4} className="text-gold" />
                                <span className="tracked text-coffee/60 text-[10px]">Your details are private</span>
                            </div>
                            <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2.5 hover:opacity-80">
                                <WhatsAppIcon size={16} color="#c8a96e" />
                                <span className="tracked text-coffee/60 text-[10px]">Prefer instant? WhatsApp us</span>
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 4 — FIND US */}
            <section className="px-6 md:px-12 py-20 md:py-24" style={{ backgroundColor: "#1a0f00" }} data-testid="contact-findus">
                <div className="max-w-[1320px] mx-auto">
                    <Reveal>
                        <div className="tracked text-gold text-[10px] mb-5">Where We Are</div>
                        <h2 className="font-editorial font-bold text-cream leading-[1.05] mb-12" style={{ fontSize: "clamp(1.9rem, 3.5vw, 2.4rem)" }}>
                            {c.map_heading || "Our home base."}
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <Reveal className="lg:col-span-5">
                            <ul className="space-y-5">
                                <DetailRow icon={<MapPin size={18} strokeWidth={1.4} className="text-gold" />} label={c.office_label || "Registered Office"} value={c.office_address || "Patna, Bihar"} />
                                <DetailRow icon={<Mail size={18} strokeWidth={1.4} className="text-gold" />} label="Email" value={c.email} href={`mailto:${c.email}`} />
                                <DetailRow icon={<Phone size={18} strokeWidth={1.4} className="text-gold" />} label="Phone" value={c.phone} href={`tel:${(c.phone || "").replace(/\s+/g, "")}`} />
                                <DetailRow icon={<Clock size={18} strokeWidth={1.4} className="text-gold" />} label="Response Hours" value={c.response_hours || "Mon – Sat, 10 AM – 7 PM"} />
                            </ul>
                            <div className="flex items-center gap-3 mt-10" data-testid="contact-socials">
                                <SocialBtn href={social.instagram} icon={<Instagram size={16} strokeWidth={1.4} />} />
                                <SocialBtn href={social.linkedin} icon={<Linkedin size={16} strokeWidth={1.4} />} />
                                <SocialBtn href={social.youtube} icon={<Youtube size={16} strokeWidth={1.4} />} />
                            </div>
                        </Reveal>
                        <Reveal delay={0.1} className="lg:col-span-7">
                            <div className="relative overflow-hidden bg-coffee-dark" style={{ border: "2px solid #c8a96e", borderRadius: 12, height: 350 }} data-testid="contact-map">
                                {c.map_embed_url ? (
                                    <iframe src={c.map_embed_url} className="absolute inset-0 w-full h-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Cafe Hideout office" allowFullScreen />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-cream/40 tracked text-[10px]">Map URL not configured</div>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}

function WhatsAppIcon({ size = 18, color = "currentColor" }) {
    return (
        <svg viewBox="0 0 32 32" width={size} height={size} fill={color} aria-hidden>
            <path d="M19.11 17.55c-.27-.14-1.62-.8-1.87-.89-.25-.09-.43-.14-.62.14-.18.27-.71.89-.87 1.07-.16.18-.32.2-.59.07-.27-.14-1.16-.43-2.2-1.36-.81-.72-1.36-1.62-1.52-1.89-.16-.27-.02-.41.12-.55.13-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.62-1.49-.84-2.04-.22-.54-.44-.46-.62-.47l-.52-.01c-.18 0-.48.07-.72.34-.25.27-.94.92-.94 2.25 0 1.32.96 2.6 1.1 2.78.14.18 1.9 2.9 4.6 4.07.64.28 1.14.44 1.53.56.64.21 1.22.18 1.69.11.51-.08 1.62-.66 1.85-1.3.23-.64.23-1.19.16-1.3-.07-.12-.25-.18-.52-.32zM16.02 5.33c-5.9 0-10.69 4.79-10.69 10.69 0 1.89.49 3.74 1.43 5.37L5.33 26.67l5.42-1.42a10.66 10.66 0 0 0 5.27 1.34c5.9 0 10.69-4.79 10.69-10.69 0-2.85-1.11-5.54-3.13-7.55a10.59 10.59 0 0 0-7.56-3.02zm0 19.59c-1.65 0-3.27-.44-4.68-1.28l-.34-.2-3.49.92.93-3.41-.22-.35a8.84 8.84 0 0 1-1.36-4.71c0-4.9 3.98-8.89 8.89-8.89 2.37 0 4.6.92 6.28 2.61a8.84 8.84 0 0 1 2.6 6.28c0 4.91-3.98 8.9-8.89 8.9z" />
        </svg>
    );
}

function QuickCard({ icon, title, body, cta }) {
    return (
        <div
            className="group p-7 md:p-8 transition-all duration-300 hover:-translate-y-1"
            style={{ backgroundColor: "#2a1a08", border: "1px solid rgba(245,230,200,0.08)" }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#c8a96e")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(245,230,200,0.08)")}
        >
            <div className="mb-6">{icon}</div>
            <h3 className="font-semibold text-gold text-xl md:text-[22px] mb-3" style={{ fontFamily: "'Poppins',sans-serif" }}>{title}</h3>
            <p className="font-body text-cream/65 text-[14.5px] leading-[1.7] mb-7">{body}</p>
            {cta}
        </div>
    );
}

function DetailRow({ icon, label, value, href, external }) {
    const inner = (
        <li className="flex items-start gap-4 group">
            <div className="mt-0.5 flex-shrink-0">{icon}</div>
            <div>
                <div className="tracked text-cream/50 text-[10px] mb-1">{label}</div>
                <div className="font-body text-cream text-[15px] group-hover:text-gold transition-colors">{value || "—"}</div>
            </div>
        </li>
    );
    if (!href) return inner;
    return <a href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{inner}</a>;
}

function SocialBtn({ href, icon }) {
    if (!href) return null;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-gold hover:text-coffee"
            style={{ border: "1px solid rgba(200,169,110,0.5)", color: "#c8a96e" }}
        >
            {icon}
        </a>
    );
}

function Field({ label, value, onChange, type = "text", textarea = false, rows = 4, testid, required = false, placeholder }) {
    return (
        <div>
            <label className="tracked text-coffee/60 text-[10px] block mb-3">{label}</label>
            {textarea ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid} required={required} placeholder={placeholder}
                    className="w-full bg-transparent border-b border-coffee/30 focus:border-gold py-3 text-coffee font-body outline-none resize-none placeholder:text-coffee/30" />
            ) : (
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required} placeholder={placeholder}
                    className="w-full bg-transparent border-b border-coffee/30 focus:border-gold py-3 text-coffee font-body outline-none placeholder:text-coffee/30" />
            )}
        </div>
    );
}
